// Healthcare Replinq Notification Service
// Handles real-time notifications for appointment updates, physician changes, and Epic FHIR data

class NotificationService {
    constructor() {
        // Enforce global singleton using window object
        if (window.healthcareNotificationService) {
            console.log('🔔 Returning existing global notification service instance');
            return window.healthcareNotificationService;
        }
        
        console.log('🔔 Creating new global notification service instance');
        
        this.wsConnection = null;
        this.subscriptions = new Map();
        this.notificationQueue = [];
        this.isConnected = false;
        this.isInitializing = false;
        
        // Store as global singleton
        window.healthcareNotificationService = this;
    }

    // Initialize WebSocket connection for real-time notifications
    async initialize() {
        // Prevent multiple simultaneous initialization attempts
        if (this.isInitializing) {
            console.log('🔔 Initialization already in progress, skipping...');
            return;
        }

        // If already connected, don't reconnect
        if (this.isConnected && this.wsConnection) {
            console.log('🔔 Already connected, skipping initialization');
            return;
        }

        console.log('🔔 Starting notification service initialization...');
        this.isInitializing = true;

        try {
            // Close existing connection if it exists
            if (this.wsConnection) {
                console.log('🔔 Closing existing WebSocket connection');
                this.wsConnection.close();
                this.wsConnection = null;
                this.isConnected = false;
            }

            console.log('🔔 Creating new WebSocket connection...');
            const { WS_BASE_URL } = require('../config/constants');
            // Connect to backend WebSocket for real-time updates
            this.wsConnection = new WebSocket(`${WS_BASE_URL}/ws/notifications`);
            
            this.wsConnection.onopen = () => {
                console.log('🔔 Notification service connected successfully');
                this.isConnected = true;
                this.isInitializing = false;
                this.processQueuedNotifications();
            };

            this.wsConnection.onmessage = (event) => {
                try {
                const notification = JSON.parse(event.data);
                this.handleNotification(notification);
                } catch (error) {
                    console.error('🔔 Failed to parse notification message:', error);
                    console.error('🔔 Raw message data:', event.data);
                }
            };

            this.wsConnection.onclose = (event) => {
                console.log('🔔 Notification service disconnected', event.code, event.reason);
                this.isConnected = false;
                this.isInitializing = false;
                this.wsConnection = null;
                
                // Only attempt to reconnect if it wasn't a manual close (code 1000)
                if (event.code !== 1000 && event.code !== 1001) {
                    console.log('🔔 Attempting to reconnect in 3 seconds...');
                    setTimeout(() => {
                        if (!this.wsConnection && !this.isInitializing) { // Double-check we don't already have a connection
                            this.initialize();
                        }
                    }, 3000);
                }
            };

            this.wsConnection.onerror = (error) => {
                console.error('🔔 Notification service error:', error);
                this.isInitializing = false;
            };

        } catch (error) {
            console.error('Failed to initialize notification service:', error);
            this.isInitializing = false;
        }
    }

    // Check if the service is ready
    isReady() {
        return this.isConnected && this.wsConnection;
    }

    // Get current status
    getStatus() {
        return {
            isConnected: this.isConnected,
            isInitializing: this.isInitializing,
            hasConnection: !!this.wsConnection,
            subscriberCount: this.subscriptions.size
        };
    }

    // Handle incoming HL7 FHIR-based notifications
    handleNotification(notification) {
        // Add safety checks for notification structure
        if (!notification || typeof notification !== 'object') {
            console.warn('🔔 Invalid notification received:', notification);
            return;
        }

        const { type, data, timestamp } = notification;

        // Ensure we have a notification type
        if (!type) {
            console.warn('🔔 Notification missing type:', notification);
            return;
        }

        // Call subscribed callbacks FIRST (for notifications page persistence)
        this.triggerSubscribedCallbacks(type, data, notification);

        switch (type) {
            case 'NEW_APPOINTMENT':
                if (data) {
                    this.notifyNewAppointment(data);
                }
                break;
            case 'APPOINTMENT_RESCHEDULED':
                if (data) {
                    this.notifyAppointmentRescheduled(data);
                }
                break;
            case 'APPOINTMENT_CANCELLED':
                if (data) {
                    this.notifyAppointmentCancelled(data);
                }
                break;
            case 'APPOINTMENT_STATUS_CHANGE':
                if (data) {
                    this.notifyAppointmentStatusChange(data);
                }
                break;
            case 'APPOINTMENT_UPDATED':
                if (data) {
                    this.notifyAppointmentUpdate(data);
                }
                break;
            case 'PHYSICIAN_ADDED':
                if (data) {
                    this.notifyPhysicianChange(data, 'added');
                }
                break;
            case 'PHYSICIAN_REMOVED':
                if (data) {
                    this.notifyPhysicianChange(data, 'removed');
                }
                break;
            case 'EPIC_DATA_REFRESH':
                if (data) {
                    this.notifyEpicDataRefresh(data);
                }
                break;
            case 'SCHEDULE_CONFLICT':
                if (data) {
                    this.notifyScheduleConflict(data);
                }
                break;
            case 'appointment_sync_changes':
                if (data) {
                    this.notifyAppointmentSyncChanges(data);
                }
                break;
            case 'background_sync_complete':
                if (data) {
                    this.notifyBackgroundSyncComplete(data);
                }
                break;
            case 'CONNECTION_ESTABLISHED':
                // Silent handler for WebSocket connection confirmation
                const message = data && data.message ? data.message : 'WebSocket connected successfully';
                console.log('🔔 WebSocket connection established:', message);
                break;
            default:
                console.log('Unknown notification type:', type, 'Data:', data);
        }
    }

    // Trigger callbacks for subscribed components (like Notifications page)
    triggerSubscribedCallbacks(type, data, fullNotification) {
        // Map backend notification types to frontend subscription types
        const typeMapping = {
            'APPOINTMENT_UPDATED': 'appointment_updated',
            'PHYSICIAN_ADDED': 'physician_added', 
            'PHYSICIAN_REMOVED': 'physician_removed',
            'EPIC_DATA_REFRESH': 'epic_data_refresh',
            'SCHEDULE_CONFLICT': 'schedule_conflict',
            'appointment_sync_changes': 'appointment_sync_changes',
            'background_sync_complete': 'background_sync_complete'
        };

        const mappedType = typeMapping[type] || type.toLowerCase();
        
        // Call subscribers for this notification type
        const callbacks = this.subscriptions.get(mappedType);
        if (callbacks && callbacks.size > 0) {
            console.log(`🔔 Triggering ${callbacks.size} callbacks for type: ${mappedType}`);
            callbacks.forEach(callback => {
                try {
                    callback(fullNotification);
                } catch (error) {
                    console.error(`🔔 Error in notification callback for ${mappedType}:`, error);
                }
            });
        }
    }

    // New appointment notifications
    notifyNewAppointment(data) {
        if (!data) return;
        
        const practitionerName = data.practitioner_name || 'Unknown Practitioner';
        const procedure = data.procedure || 'New Appointment';
        const appointmentDate = data.start_date ? new Date(data.start_date).toLocaleDateString() : '';
        const message = `🆕 New appointment scheduled: ${practitionerName} - ${procedure} on ${appointmentDate}`;
        
        this.showNotification(message, 'success', {
            icon: '🆕',
            duration: 6000,
            action: data.appointment_id ? () => this.navigateToCalendar(data.appointment_id) : null
        });

        // Trigger UI refresh for affected components
        this.triggerComponentRefresh('calendar', data);
        this.triggerComponentRefresh('dashboard', data);
    }

    // Appointment rescheduled notifications
    notifyAppointmentRescheduled(data) {
        if (!data) return;
        
        const practitionerName = data.practitioner_name || 'Unknown Practitioner';
        const oldDate = data.old_date ? new Date(data.old_date).toLocaleDateString() : '';
        const newDate = data.new_date ? new Date(data.new_date).toLocaleDateString() : '';
        const message = `🔄 Appointment rescheduled: ${practitionerName} moved from ${oldDate} to ${newDate}`;
        
        this.showNotification(message, 'warning', {
            icon: '🔄',
            duration: 7000,
            action: data.appointment_id ? () => this.navigateToCalendar(data.appointment_id) : null
        });

        // Trigger UI refresh for affected components
        this.triggerComponentRefresh('calendar', data);
        this.triggerComponentRefresh('dashboard', data);
    }

    // Appointment cancelled notifications
    notifyAppointmentCancelled(data) {
        if (!data) return;
        
        const practitionerName = data.practitioner_name || 'Unknown Practitioner';
        const originalDate = data.original_date ? new Date(data.original_date).toLocaleDateString() : '';
        const reason = data.cancellation_reason ? ` (${data.cancellation_reason})` : '';
        const message = `❌ Appointment cancelled: ${practitionerName} on ${originalDate}${reason}`;
        
        this.showNotification(message, 'error', {
            icon: '❌',
            duration: 8000,
            persistent: true,
            action: () => this.navigateToCalendar()
        });

        // Trigger UI refresh for affected components
        this.triggerComponentRefresh('calendar', data);
        this.triggerComponentRefresh('dashboard', data);
    }

    // Appointment status change notifications
    notifyAppointmentStatusChange(data) {
        if (!data) return;
        
        const practitionerName = data.practitioner_name || 'Unknown Practitioner';
        const oldStatus = data.old_status || '';
        const newStatus = data.new_status || '';
        const statusEmoji = this.getStatusEmoji(newStatus);
        const message = `${statusEmoji} Status updated: ${practitionerName} appointment ${oldStatus} → ${newStatus}`;
        
        const notificationType = newStatus === 'cancelled' ? 'error' : 
                               newStatus === 'confirmed' ? 'success' : 'info';
        
        this.showNotification(message, notificationType, {
            icon: statusEmoji,
            duration: 5000,
            action: data.appointment_id ? () => this.navigateToCalendar(data.appointment_id) : null
        });

        // Trigger UI refresh for affected components
        this.triggerComponentRefresh('calendar', data);
        this.triggerComponentRefresh('dashboard', data);
    }

    // Helper method to get emoji for appointment status
    getStatusEmoji(status) {
        const statusMap = {
            'booked': '📅',
            'confirmed': '✅', 
            'arrived': '🏥',
            'in-progress': '⚕️',
            'completed': '✅',
            'cancelled': '❌',
            'no-show': '👻',
            'rescheduled': '🔄'
        };
        return statusMap[status?.toLowerCase()] || '📅';
    }

    // Appointment update notifications (HL7 FHIR Appointment resource changes)
    notifyAppointmentUpdate(data) {
        if (!data) return;
        
        const practitionerName = data.practitioner_name || 'Unknown Practitioner';
        const procedure = data.procedure || 'Unknown Procedure';
        const message = `📅 Appointment updated: ${practitionerName} - ${procedure}`;
        
        this.showNotification(message, 'info', {
            icon: '📅',
            action: data.appointment_id ? () => this.navigateToCalendar(data.appointment_id) : null
        });

        // Trigger UI refresh for affected components
        this.triggerComponentRefresh('calendar', data);
        this.triggerComponentRefresh('dashboard', data);
    }

    // Physician change notifications
    notifyPhysicianChange(data, action) {
        if (!data) return;
        
        const fullName = data.full_name || 'Unknown Physician';
        const actionText = action === 'added' ? 'added to' : 'removed from';
        const icon = action === 'added' ? '👨‍⚕️' : '❌';
        const message = `${icon} Dr. ${fullName} ${actionText} your dashboard`;
        
        this.showNotification(message, action === 'added' ? 'success' : 'warning', {
            icon,
            duration: 5000,
            action: () => this.navigateToAdmin()
        });

        // Refresh admin dashboard
        this.triggerComponentRefresh('admin', data);
    }

    // Epic FHIR data refresh notifications
    notifyEpicDataRefresh(data) {
        if (!data) return;
        
        const appointmentsCount = data.appointments_count || 0;
        const message = `🔄 Epic FHIR data updated: ${appointmentsCount} appointments refreshed`;
        
        this.showNotification(message, 'info', {
            icon: '🔄',
            duration: 3000
        });

        // Refresh all components with new Epic data
        this.triggerComponentRefresh('all', data);
    }

    // Schedule conflict notifications
    notifyScheduleConflict(data) {
        if (!data) return;
        
        const practitionerName = data.practitioner_name || 'Unknown Practitioner';
        const message = `⚠️ Schedule conflict detected: ${practitionerName} has overlapping appointments`;
        
        this.showNotification(message, 'error', {
            icon: '⚠️',
            duration: 8000,
            persistent: true,
            action: data.conflict_date ? () => this.navigateToCalendar(data.conflict_date) : null
        });
    }

    // Appointment sync changes notifications (NEW)
    notifyAppointmentSyncChanges(data) {
        if (!data) return;
        
        const { practitioner_name, changes = [], summary, total_changes = 0 } = data;
        
        // Determine notification style based on change types
        const hasNew = changes.some(c => c && c.type === 'new');
        const hasUpdated = changes.some(c => c && c.type === 'updated');
        const hasCancelled = changes.some(c => c && c.type === 'cancelled');
        
        let icon = '📅';
        let notificationType = 'info';
        
        if (hasNew) {
            icon = '🆕';
            notificationType = 'success';
        } else if (hasCancelled) {
            icon = '❌';
            notificationType = 'warning';
        } else if (hasUpdated) {
            icon = '🔄';
        }
        
        const message = summary ? `${icon} ${summary}` : `${icon} Appointment changes detected`;
        
        this.showNotification(message, notificationType, {
            icon,
            duration: 8000,
            persistent: total_changes > 3, // Persist if many changes
            action: () => this.navigateToAdmin()
        });

        // Show detailed breakdown in console
        console.log('📅 Appointment sync changes:', data);
        
        // Trigger dashboard refresh
        this.triggerComponentRefresh('admin', data);
    }

    // Background sync complete notifications (NEW)
    notifyBackgroundSyncComplete(data) {
        if (!data) return;
        
        const { users_affected = 0, message = 'Background sync completed' } = data;
        
        if (users_affected > 0) {
            this.showNotification(`🔄 ${message}`, 'info', {
                icon: '🔄',
                duration: 4000,
                action: () => this.navigateToAdmin()
            });
            
            // Refresh all components with updated data
            this.triggerComponentRefresh('all', data);
        }
    }

    // Show browser notification
    showNotification(message, type = 'info', options = {}) {
        const {
            icon = '🔔',
            duration = 4000,
            persistent = false,
            action = null
        } = options;

        // Browser notification (if permission granted)
        if (Notification.permission === 'granted') {
            const notification = new Notification('Healthcare Replinq', {
                body: message,
                icon: '/logo192.png',
                badge: '/logo192.png'
            });

            if (action) {
                notification.onclick = action;
            }

            if (!persistent) {
                setTimeout(() => notification.close(), duration);
            }
        }

        // In-app toast notification
        this.showToast(message, type, { icon, duration, action });
    }

    // Show in-app toast notification
    showToast(message, type, options) {
        // Create toast element
        const toast = document.createElement('div');
        toast.className = `notification-toast notification-${type}`;
        toast.innerHTML = `
            <div class="notification-content">
                <span class="notification-icon">${options.icon}</span>
                <span class="notification-message">${message}</span>
                <button class="notification-close" onclick="this.parentElement.parentElement.remove()">×</button>
            </div>
        `;

        // Add click action if provided
        if (options.action) {
            toast.style.cursor = 'pointer';
            toast.onclick = options.action;
        }

        // Add to page
        document.body.appendChild(toast);

        // Auto-remove after duration
        setTimeout(() => {
            if (toast.parentElement) {
                toast.remove();
            }
        }, options.duration);
    }

    // Subscribe to specific notification types
    subscribe(type, callback) {
        if (!this.subscriptions.has(type)) {
            this.subscriptions.set(type, new Set());
        }
        this.subscriptions.get(type).add(callback);
    }

    // Unsubscribe from notifications
    unsubscribe(type, callback) {
        if (this.subscriptions.has(type)) {
            this.subscriptions.get(type).delete(callback);
        }
    }

    // Trigger component refresh
    triggerComponentRefresh(component, data) {
        const callbacks = this.subscriptions.get(`refresh_${component}`);
        if (callbacks) {
            callbacks.forEach(callback => callback(data));
        }
    }

    // Navigation helpers
    navigateToCalendar(appointmentId = null) {
        const url = appointmentId ? `/calendar?appointment=${appointmentId}` : '/calendar';
        window.location.href = url;
    }

    navigateToAdmin() {
        window.location.href = '/admin';
    }

    // Request browser notification permission
    async requestPermission() {
        if ('Notification' in window) {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        }
        return false;
    }

    // Process queued notifications when connection is restored
    processQueuedNotifications() {
        while (this.notificationQueue.length > 0) {
            const notification = this.notificationQueue.shift();
            this.handleNotification(notification);
        }
    }

    // Queue notification if not connected
    queueNotification(notification) {
        if (this.isConnected) {
            this.handleNotification(notification);
        } else {
            this.notificationQueue.push(notification);
        }
    }

    // Properly disconnect and cleanup
    disconnect() {
        console.log('🔔 Manually disconnecting notification service');
        if (this.wsConnection) {
            this.wsConnection.close(1000, 'Manual disconnect'); // Normal closure
            this.wsConnection = null;
            this.isConnected = false;
        }
    }
}

// Ensure we always export the same global singleton instance
const getNotificationService = () => {
    if (window.healthcareNotificationService) {
        return window.healthcareNotificationService;
    }
    return new NotificationService();
};

export default getNotificationService(); 